// @ts-strict-ignore
import React from "react";
import { Box, Button, Input } from "@saleor/macaw-ui-next";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { FilterTabs, FilterTab } from "@dashboard/components/TableFilter";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import { readonlyTextCell } from "@dashboard/components/Datagrid/customCells/cells";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import { BulkDeleteButton } from "@dashboard/components/BulkDeleteButton";
import {
  useVendorListedProductsQuery,
  useRemoveProductPromotionMutation,
  useSetDefaultMarkupMutation,
  useGetVendorDefaultMarkupQuery,
  useProductListQuery,
  useProductBulkDeleteMutation,
  useBulkVendorProductUpdateMutation,
} from "@dashboard/graphql";
import useListSettings from "@dashboard/hooks/useListSettings";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { sectionNames } from "@dashboard/intl";
import { ListViews, RelayToFlat } from "@dashboard/types";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { ProductListQuery } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { useIntl, FormattedMessage } from "react-intl";

import { vendorOfferingsUrl, VendorListUrlQueryParams } from "../../urls";
import {
  createGetCellContent,
  vendorOfferingsStaticColumnsAdapter,
  VendorListedProduct,
} from "./datagrid";

interface VendorOfferingsProps {
  params?: VendorListUrlQueryParams; // URL parameters for pagination/filtering
}

export const VendorOfferings = ({ params = {} }: VendorOfferingsProps) => {
  const intl = useIntl();
  const notify = useNotifier();
  const { updateListSettings, settings } = useListSettings(ListViews.VENDOR_ASSOCIATED_PRODUCTS);
  const datagridState = useDatagridChangeState();

  // Tab state management
  const [currentTab, setCurrentTab] = React.useState(0);

  // State for tracking selected products
  const [selectedProductIds, setSelectedProductIds] = React.useState<string[]>([]);

  // Mutation hook for removing products
  const [removeProductPromotion, removeProductPromotionOpts] = useRemoveProductPromotionMutation({
    onCompleted: data => {
      if (data?.removeProductPromotion?.success) {
        notify({
          status: "success",
          text: "Successfully removed the products",
        });
        setSelectedProductIds([]); // Clear selection
        refetch(); // Refresh the product list
      } else {
        notify({
          status: "error",
          text: data?.removeProductPromotion?.message || "Failed to remove products",
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        text: error.message || "Failed to remove products",
      });
    },
  });

  // Pagination reset when parameters change
  usePaginationReset(vendorOfferingsUrl, params, settings.rowNumber);

  // Create pagination state from URL parameters and settings
  const paginationState = createPaginationState(settings.rowNumber, params);

  // GraphQL query variables
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
    }),
    [paginationState],
  );

  // GraphQL query to fetch vendor products with pagination
  const { data, loading, error, refetch } = useVendorListedProductsQuery({
    displayLoader: true,
    variables: queryVariables,
  });

  // Create paginator context values
  const paginationValues = usePaginator({
    pageInfo: data?.vendorListedProducts?.pageInfo,
    paginationState,
    queryString: params,
  });

  // Log any GraphQL errors for debugging
  if (error) {
    console.error("GraphQL Error in VendorOfferings:", error);
  }

  // Extract products from the GraphQL response
  const products: VendorListedProduct[] = React.useMemo(() => {
    return mapEdgesToItems(data?.vendorListedProducts) || [];
  }, [data?.vendorListedProducts]);

  // Datagrid columns and content
  const availableColumns = React.useMemo(() => vendorOfferingsStaticColumnsAdapter(intl), [intl]);

  const { visibleColumns, handlers } = useColumns({
    staticColumns: availableColumns,
    selectedColumns: ["id", "name", "createdAt", "updatedAt"],
    onSave: columns => updateListSettings("columns", columns),
  });

  const getCellContent = React.useMemo(
    () => createGetCellContent(products, visibleColumns),
    [products, visibleColumns],
  );

  const handleRowSelectionChange = React.useCallback(
    (rows: number[]) => {
      const selectedIds = rows.map(row => products[row]?.id).filter(Boolean);
      setSelectedProductIds(selectedIds);
      console.log("Selected rows:", selectedIds);
    },
    [products],
  );

  const handleRemoveProducts = React.useCallback(async () => {
    if (selectedProductIds.length === 0) {
      notify({
        status: "error",
        text: "Please select products to remove",
      });
      return;
    }

    try {
      await removeProductPromotion({
        variables: {
          productIds: selectedProductIds,
        },
      });
    } catch (error) {
      // Error is already handled in the onError callback
      console.error("Remove products error:", error);
    }
  }, [selectedProductIds, removeProductPromotion, notify]);

  // Handle row click to open product in new tab
  const handleRowClick = React.useCallback(
    (item: [number, number]) => {
      const [column, row] = item;
      const product = products[row];
      if (product?.id) {
        const frontendBaseUrl = process.env.FRONT_END_BASE_URL;
        console.log("Frontend Base URL:", frontendBaseUrl);
        if (frontendBaseUrl) {
          const productUrl = `${frontendBaseUrl}/product-detail/${product.id}`;
          window.open(productUrl, "_blank", "noopener,noreferrer");
        } else {
          console.warn("FRONT_END_BASE_URL environment variable is not set");
          notify({
            status: "error",
            text: "Frontend URL not configured",
          });
        }
      }
    },
    [products, notify],
  );

  if (loading) {
    return (
      <ListPageLayout>
        <TopNav title={intl.formatMessage(sectionNames.vendorOfferings)} />
        <Box paddingX={9} paddingY={6}>
          <DashboardCard>
            <Box padding={4} textAlign="center">
              Loading vendor offerings...
            </Box>
          </DashboardCard>
        </Box>
      </ListPageLayout>
    );
  }

  if (error) {
    return (
      <ListPageLayout>
        <TopNav title={intl.formatMessage(sectionNames.vendorOfferings)} />
        <Box paddingX={9} paddingY={6}>
          <DashboardCard>
            <Box padding={4} style={{ textAlign: "center", color: "red" }}>
              Error loading vendor offerings: {error.message}
            </Box>
          </DashboardCard>
        </Box>
      </ListPageLayout>
    );
  }

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <ListPageLayout>
        <TopNav title={intl.formatMessage(sectionNames.vendorOfferings)}>
          {currentTab === 0 && (
            <Box display="flex" gap={3} alignItems="center">
              <Button
                onClick={handleRemoveProducts}
                disabled={selectedProductIds.length === 0 || removeProductPromotionOpts.loading}
              >
                {removeProductPromotionOpts.loading
                  ? "Removing..."
                  : `Remove Products${selectedProductIds.length > 0 ? ` (${selectedProductIds.length})` : ""}`}
              </Button>
            </Box>
          )}
        </TopNav>

        <DashboardCard>
          <FilterTabs currentTab={currentTab}>
            <FilterTab label="Associated Products" onClick={() => setCurrentTab(0)} />
            <FilterTab label="My Default Markup" onClick={() => setCurrentTab(1)} />
            <FilterTab label="Catalog" onClick={() => setCurrentTab(2)} />
          </FilterTabs>

          {currentTab === 0 && (
            <DatagridChangeStateContext.Provider value={datagridState}>
              <Datagrid
                readonly
                loading={loading}
                rowMarkers="checkbox-visible"
                columnSelect="single"
                hasRowHover={true}
                verticalBorder={false}
                rows={products.length}
                availableColumns={visibleColumns}
                emptyText={intl.formatMessage({
                  defaultMessage: "No vendor products found",
                  id: "vendorProductsEmpty",
                })}
                // onRowClick={handleRowClick}
                onRowSelectionChange={handleRowSelectionChange}
                getCellContent={getCellContent}
                getCellError={() => false}
                selectionActions={() => null}
                menuItems={() => []}
                onColumnMoved={handlers.onMove}
                onColumnResize={handlers.onResize}
              />

              <Box paddingX={6}>
                <TablePaginationWithContext
                  component="div"
                  settings={settings}
                  disabled={loading}
                  onUpdateListSettings={updateListSettings}
                />
              </Box>
            </DatagridChangeStateContext.Provider>
          )}

          {currentTab === 1 && <MyDefaultMarkupTab />}

          {currentTab === 2 && <CatalogTab />}
        </DashboardCard>
      </ListPageLayout>
    </PaginatorContext.Provider>
  );
};

// Component for the MyDefaultMarkup tab
const MyDefaultMarkupTab = () => {
  const notify = useNotifier();
  const [markupValue, setMarkupValue] = React.useState("");
  const [applyToExisting, setApplyToExisting] = React.useState(false);

  // Query to get current default markup settings
  const { loading: loadingMarkup, refetch: refetchMarkup } = useGetVendorDefaultMarkupQuery({
    onCompleted: data => {
      if (data?.vendorDefaultMarkup?.success && data?.vendorDefaultMarkup?.data) {
        try {
          const parsedData = JSON.parse(data.vendorDefaultMarkup.data);
          if (parsedData.markup) {
            setMarkupValue(parsedData.markup.toString());
          }
          setApplyToExisting(data.vendorDefaultMarkup.useDefaultMarkup || false);
        } catch (error) {
          console.error("Error parsing default markup data:", error);
        }
      }
    },
    onError: error => {
      console.error("Error fetching default markup:", error);
    },
  });

  const [setDefaultMarkup, setDefaultMarkupOpts] = useSetDefaultMarkupMutation({
    onCompleted: data => {
      const result = data?.setDefaultMarkup;
      if (result?.success) {
        notify({
          status: "success",
          text: "Updated successfully",
        });
        refetchMarkup();
      } else {
        notify({
          status: "error",
          text: result?.message || "Failed to update markup",
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        text: error?.message || "Failed to update markup",
      });
    },
  });

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!markupValue.trim()) {
        notify({
          status: "error",
          text: "Please enter a markup percentage",
        });
        return;
      }

      try {
        await setDefaultMarkup({
          variables: {
            markup: markupValue.trim(),
            applyToExisting,
          },
        });
      } catch (error) {
        console.error("Set default markup error:", error);
      }
    },
    [markupValue, applyToExisting, setDefaultMarkup, notify],
  );

  if (loadingMarkup) {
    return (
      <Box padding={6} textAlign="center">
        <p>Loading default markup settings...</p>
      </Box>
    );
  }

  return (
    <Box paddingTop={0} paddingBottom={6} paddingLeft={6} paddingRight={6}>
      <Box marginBottom={6}>
        <h2>Set Default Markup</h2>
        <p>Configure the default markup percentage for your vendor products.</p>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box display="flex" flexDirection="column" gap={4} style={{ maxWidth: "600px" }}>
          <Box>
            <label
              htmlFor="markup-input"
              style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}
            >
              Markup Percentage
            </label>
            <Input
              id="markup-input"
              type="text"
              value={markupValue}
              onChange={e => setMarkupValue(e.target.value)}
              placeholder="e.g., 12.50"
              disabled={setDefaultMarkupOpts.loading || loadingMarkup}
            />
            <p style={{ fontSize: "14px", color: "#666", marginTop: "4px", margin: 0 }}>
              Enter the markup percentage (e.g., 12.50 for 12.50%)
            </p>
          </Box>

          <Box display="flex" alignItems="flex-start" gap={2}>
            <input
              type="checkbox"
              id="apply-existing"
              checked={applyToExisting}
              onChange={e => setApplyToExisting(e.target.checked)}
              disabled={setDefaultMarkupOpts.loading || loadingMarkup}
              style={{ marginTop: "2px" }}
            />
            <Box>
              <label htmlFor="apply-existing" style={{ fontWeight: 500 }}>
                Apply to existing listings without custom markup
              </label>
              <p style={{ fontSize: "14px", color: "#666", margin: 0, marginTop: "4px" }}>
                When enabled, this markup will be applied to all existing product listings that
                don't have a custom markup set.
              </p>
            </Box>
          </Box>

          <Box marginTop={2}>
            <Button
              type="submit"
              disabled={setDefaultMarkupOpts.loading || loadingMarkup || !markupValue.trim()}
            >
              {setDefaultMarkupOpts.loading ? "Setting Markup..." : "Set Default Markup"}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

// Component for the Catalog tab - displays main product catalog
const CatalogTab = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const { updateListSettings, settings } = useListSettings(ListViews.VENDOR_ASSOCIATED_PRODUCTS);
  const datagridState = useDatagridChangeState();

  // State for tracking selected products in catalog
  const [selectedProductIds, setSelectedProductIds] = React.useState<string[]>([]);

  // Create pagination state
  const paginationState = createPaginationState(settings.rowNumber, {});

  // GraphQL query variables for main products catalog
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: {},
      channel: null,
      hasChannel: false,
      includeCategories: false,
      includeCollections: false,
    }),
    [paginationState],
  );

  // GraphQL query to fetch main products catalog
  const { data, loading, error, refetch } = useProductListQuery({
    displayLoader: true,
    variables: queryVariables,
  });

  // Extract products from the GraphQL response
  const products = React.useMemo(() => {
    return mapEdgesToItems(data?.products) || [];
  }, [data?.products]);

  // Create paginator context values
  const paginationValues = usePaginator({
    pageInfo: data?.products?.pageInfo,
    paginationState,
    queryString: {},
  });

  // Create columns for main product catalog (price column commented out)
  const availableColumns = React.useMemo(
    () => [
      {
        id: "name",
        title: "Product",
        width: 300,
      },
      // {
      //   id: "description",
      //   title: "Description",
      //   width: 300,
      // },
      // {
      //   id: "price",
      //   title: "Price",
      //   width: 150,
      // },
      {
        id: "type",
        title: "Type",
        width: 200,
      },
      {
        id: "updatedAt",
        title: "Last updated",
        width: 200,
      },
      {
        id: "created",
        title: "Created",
        width: 200,
      },
    ],
    [],
  );

  const { visibleColumns } = useColumns({
    staticColumns: availableColumns,
    selectedColumns: ["name", "description", "type", "updatedAt", "created"], // removed "price" from selected columns
    onSave: () => {},
  });

  // Create cell content for main product catalog
  const getCatalogCellContent = React.useMemo(
    () => createProductCatalogCellContent(products, visibleColumns),
    [products, visibleColumns],
  );

  // Handle row selection change
  const handleRowSelectionChange = React.useCallback(
    (rows: number[]) => {
      const selectedIds = rows.map(row => products[row]?.id).filter(Boolean);
      setSelectedProductIds(selectedIds);
      console.log("Selected catalog products:", selectedIds);
    },
    [products],
  );

  // Handle row click to open product in new tab for catalog
  const handleCatalogRowClick = React.useCallback(
    (item: [number, number]) => {
      const [column, row] = item;
      const product = products[row];
      console.log("Product:", product);
      if (product?.id) {
        const frontendBaseUrl = process.env.FRONT_END_BASE_URL;
        if (frontendBaseUrl) {
          const productUrl = `${frontendBaseUrl}/product-detail/${product.id}`;
          window.open(productUrl, "_blank", "noopener,noreferrer");
        } else {
          console.warn("FRONT_END_BASE_URL environment variable is not set");
          notify({
            status: "error",
            text: "Frontend URL not configured",
          });
        }
      }
    },
    [products, notify],
  );

  // Bulk delete mutation - direct delete without dialog
  const [productBulkDelete] = useProductBulkDeleteMutation({
    onCompleted: data => {
      if (data?.productBulkDelete?.errors?.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(
            {
              id: "productBulkDeleteSuccess",
              defaultMessage: "Successfully deleted {count} products",
            },
            { count: selectedProductIds.length },
          ),
        });
        setSelectedProductIds([]);
        refetch();
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            id: "productBulkDeleteError",
            defaultMessage: "Failed to delete products",
          }),
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        text: error.message || "Failed to delete products",
      });
    },
  });

  // Bulk vendor product update mutation (attach to vendor)
  const [bulkVendorProductUpdate, bulkVendorProductUpdateOpts] = useBulkVendorProductUpdateMutation(
    {
      onCompleted: data => {
        if (data?.bulkVendorProductUpdate?.success) {
          notify({
            status: "success",
            text: intl.formatMessage(
              {
                id: "lGwzRB",
                defaultMessage: "Successfully attached {count} products to vendor",
              },
              { count: selectedProductIds.length },
            ),
          });
          setSelectedProductIds([]);
          refetch();
        } else {
          notify({
            status: "error",
            text: intl.formatMessage({
              id: "NUhQaF",
              defaultMessage: "Failed to attach products to vendor",
            }),
          });
        }
      },
      onError: error => {
        notify({
          status: "error",
          text: error.message || "Failed to attach products to vendor",
        });
      },
    },
  );

  // Handle delete products - direct delete without dialog
  const handleDeleteProducts = React.useCallback(() => {
    if (selectedProductIds.length === 0) {
      notify({
        status: "error",
        text: "Please select products to delete",
      });
      return;
    }

    // Directly delete without showing dialog
    productBulkDelete({
      variables: {
        ids: selectedProductIds,
      },
    });
  }, [selectedProductIds, productBulkDelete, notify]);

  // Handle attach to vendor
  const handleAttachToVendor = React.useCallback(() => {
    if (selectedProductIds.length === 0) {
      notify({
        status: "error",
        text: "Please select products to attach to vendor",
      });
      return;
    }

    bulkVendorProductUpdate({
      variables: {
        productIds: selectedProductIds,
        markup: "0",
        useDefaultMarkup: false,
      },
    });
  }, [selectedProductIds, bulkVendorProductUpdate, notify]);

  if (loading) {
    return (
      <Box padding={6} textAlign="center">
        <p>Loading catalog...</p>
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding={6} textAlign="center">
        <p style={{ color: "red" }}>Error loading catalog: {error.message}</p>
      </Box>
    );
  }

  return (
    <PaginatorContext.Provider value={paginationValues}>
      {/* Action buttons above the table */}
      <Box paddingX={6} display="flex" justifyContent="flex-end" gap={2}>
        {selectedProductIds.length > 0 && (
          <>
            <BulkDeleteButton onClick={handleDeleteProducts}>
              <FormattedMessage defaultMessage="Delete products" id="uwk5e9" />
            </BulkDeleteButton>
            <Button
              onClick={handleAttachToVendor}
              disabled={bulkVendorProductUpdateOpts.loading}
              variant="secondary"
            >
              {bulkVendorProductUpdateOpts.loading
                ? "Attaching..."
                : `Attach to Vendor (${selectedProductIds.length})`}
            </Button>
          </>
        )}
      </Box>
      <DatagridChangeStateContext.Provider value={datagridState}>
        <Datagrid
          readonly
          loading={loading}
          rowMarkers="checkbox-visible" // Enable checkboxes
          columnSelect="single"
          hasRowHover={true}
          verticalBorder={false}
          rows={products.length}
          availableColumns={visibleColumns}
          emptyText={intl.formatMessage({
            defaultMessage: "No products found in catalog",
            id: "catalogEmpty",
          })}
          // onRowClick={handleCatalogRowClick}
          onRowSelectionChange={handleRowSelectionChange} // Add row selection handler
          getCellContent={getCatalogCellContent}
          getCellError={() => false}
          selectionActions={() => null}
          menuItems={() => []}
          onColumnMoved={() => {}}
          onColumnResize={() => {}}
        />

        <Box paddingX={6}>
          <TablePaginationWithContext
            component="div"
            settings={settings}
            disabled={loading}
            onUpdateListSettings={updateListSettings}
          />
        </Box>
      </DatagridChangeStateContext.Provider>
    </PaginatorContext.Provider>
  );
};

// Helper function to create cell content for main product catalog (price case commented out)
function createProductCatalogCellContent(
  products: RelayToFlat<ProductListQuery["products"]>,
  columns: { id: string; title: string; width: number }[],
) {
  return ([column, row]: [number, number]) => {
    const columnId = columns[column]?.id;
    const rowData = products[row];

    if (!columnId || !rowData) {
      return readonlyTextCell("");
    }

    switch (columnId) {
      case "name":
        return readonlyTextCell(rowData.name || "");
      // case "description":
      //   return readonlyTextCell(rowData.description || rowData.name || "");
      // case "price":
      //   return readonlyTextCell("Select channel");
      case "type":
        return readonlyTextCell(rowData.productType?.name || "Default Type");
      case "updatedAt":
        return readonlyTextCell(
          rowData.updatedAt ? new Date(rowData.updatedAt).toLocaleDateString() : "",
        );
      case "created":
        return readonlyTextCell(
          rowData.created ? new Date(rowData.created).toLocaleDateString() : "",
        );
      default:
        return readonlyTextCell("");
    }
  };
}

export default VendorOfferings;
